// Google Analytics utility functions
// Requires gtag to be loaded via script tag in the HTML head

/**
 * Check if Google Analytics is available and properly configured
 */
export const isGAAvailable = () => {
  return (
    typeof window !== 'undefined' && 
    typeof window.gtag === 'function' &&
    import.meta.env.VITE_GOOGLE_ANALYTICS &&
    import.meta.env.VITE_GOOGLE_ANALYTICS.trim() !== ''
  );
};

/**
 * Track a page view
 * @param {string} page_title - The title of the page
 * @param {string} page_location - The URL of the page
 */
export const trackPageView = (page_title, page_location) => {
  try {
    if (isGAAvailable()) {
      window.gtag('config', import.meta.env.VITE_GOOGLE_ANALYTICS, {
        page_title,
        page_location,
      });
    }
  } catch (error) {
    console.warn('Google Analytics tracking failed:', error);
  }
};

/**
 * Track a custom event
 * @param {string} action - The action being tracked
 * @param {Object} parameters - Additional parameters for the event
 */
export const trackEvent = (action, parameters = {}) => {
  try {
    if (isGAAvailable()) {
      window.gtag('event', action, parameters);
    }
  } catch (error) {
    console.warn('Google Analytics event tracking failed:', error);
  }
};

/**
 * Track user engagement events
 */
export const trackEngagement = {
  // Event creation
  eventCreated: (eventType = 'garage_sale') => {
    trackEvent('event_created', {
      event_category: 'engagement',
      event_type: eventType,
    });
  },

  // Sale creation
  saleCreated: (eventId) => {
    trackEvent('sale_created', {
      event_category: 'engagement',
      event_id: eventId,
    });
  },

  // Search actions
  searchPerformed: (searchType, query) => {
    trackEvent('search', {
      event_category: 'engagement',
      search_type: searchType,
      search_term: query,
    });
  },

  // Map interactions
  mapInteraction: (action, location) => {
    trackEvent('map_interaction', {
      event_category: 'engagement',
      map_action: action,
      location: location,
    });
  },

  // User authentication
  userSignUp: () => {
    trackEvent('sign_up', {
      event_category: 'engagement',
      method: 'email',
    });
  },

  userLogin: () => {
    trackEvent('login', {
      event_category: 'engagement',
      method: 'email',
    });
  },

  // File uploads
  imageUploaded: (context) => {
    trackEvent('image_upload', {
      event_category: 'engagement',
      upload_context: context,
    });
  },

  // Navigation
  externalLinkClicked: (url) => {
    trackEvent('click', {
      event_category: 'engagement',
      link_url: url,
      outbound: true,
    });
  },
};

/**
 * Track conversion events
 */
export const trackConversion = {
  // Event published
  eventPublished: (eventId, eventType) => {
    trackEvent('event_published', {
      event_category: 'conversion',
      event_id: eventId,
      event_type: eventType,
      value: 1,
    });
  },

  // Sale published
  salePublished: (saleId, eventId) => {
    trackEvent('sale_published', {
      event_category: 'conversion',
      sale_id: saleId,
      event_id: eventId,
      value: 1,
    });
  },
};

/**
 * Set user properties
 * @param {Object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  try {
    if (isGAAvailable()) {
      window.gtag('config', import.meta.env.VITE_GOOGLE_ANALYTICS, {
        custom_map: properties,
      });
    }
  } catch (error) {
    console.warn('Google Analytics user properties failed:', error);
  }
};

/**
 * Track timing events
 * @param {string} name - The timing variable name
 * @param {number} value - The timing value in milliseconds
 * @param {string} category - The timing category
 */
export const trackTiming = (name, value, category = 'performance') => {
  try {
    if (isGAAvailable()) {
      window.gtag('event', 'timing_complete', {
        name: name,
        value: value,
        event_category: category,
      });
    }
  } catch (error) {
    console.warn('Google Analytics timing tracking failed:', error);
  }
};

export default {
  isGAAvailable,
  trackPageView,
  trackEvent,
  trackEngagement,
  trackConversion,
  setUserProperties,
  trackTiming,
}; 